import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Box,
  Divider,
  useTheme,
  AppBar,
  Toolbar,
  TextField,
  Avatar,
  Chip,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  MoreVert,
  Visibility,
  Person,
  Edit,
  Dashboard as DashboardIcon,
  Logout,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { fetchBatches } from "../api/tnsdc";

// Styled Components
const DashboardCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.08)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 8px 24px 0 rgba(0,0,0,0.12)",
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  justifyContent: "flex-end",
  ...theme.mixins.toolbar,
}));

// Logo component - replace with your actual logo
const Logo = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      mr: 2,
      p: 1,
      color: "white",
      borderRadius: 1,
      fontWeight: "bold",
    }}
  >
    <img src="/navriti-logo.png" alt="TNSDC Logo" style={{ height: 40 }} />
  </Box>
);

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedBatch, setSelectedBatch] = React.useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);
  const [user, setUser] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [logoutSnackbarOpen, setLogoutSnackbarOpen] = useState(false);

  const open = Boolean(anchorEl);
  const isProfileMenuOpen = Boolean(profileAnchorEl);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = JSON.parse(localStorage.getItem("user"));

    if (!token || !userData) {
      navigate("/", { replace: true });
    } else {
      setUser(userData);
    }

    fetchBatches()
      .then((res) => {
        const arr = res.data.result?.GetBatchInfo?.BatchArray || [];
        setBatches(arr);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching batches", err);
        setLoading(false);
      });
  }, [navigate]);

  const handleMenuClick = (event, batch) => {
    setAnchorEl(event.currentTarget);
    setSelectedBatch(batch);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedBatch(null);
  };

  const handleOptionClick = (option, batch) => {
    if (option === "Assessor Details") {
      navigate(`/assessor-details/${batch.BatchCode}`);
    } else if (option === "View Details") {
      navigate(`/batch/${batch.BatchId}`);
    }
    handleClose();
  };

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  setLogoutSnackbarOpen(true);
  handleProfileClose();
  setTimeout(() => {
    navigate("/", { replace: true });
  }, 1500); 
};


  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setLogoutSnackbarOpen(false);
  };

  const getInitials = (nameOrEmail) => {
    if (!nameOrEmail) return "U";
    const name = nameOrEmail.split("@")[0];
    return name[0].toUpperCase();
  };

  const filteredBatches = batches.filter((batch) =>
    batch.BatchId.toString().includes(searchTerm)
  );

  const drawer = (
    <div>
      <DrawerHeader>
        <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center" }}>
          TNSDC Portal
        </Typography>
      </DrawerHeader>
      <Divider />
      <List>
        <ListItem button selected>
          <ListItemIcon>
            <DashboardIcon sx={{ color: "primary.main" }} />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Navigation Drawer */}
      <Box component="nav" sx={{ width: 200, flexShrink: 0 }}>
        <Drawer
          variant="permanent"
          sx={{
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: 200 },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* App Bar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{ backgroundColor: "white", color: "text.primary" }}
        >
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by Batch ID...."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "50px",
                  mr: 2,
                  width: { xs: 150, sm: 250, md: 300 },
                }}
                InputProps={{
                  sx: { borderRadius: "50px", px: 2 },
                }}
              />
            </Box>
            {/* Empty flex box to push logo and profile to the right */}
            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Logo />
              <IconButton onClick={handleProfileClick} sx={{ ml: 1 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: theme.palette.primary.main,
                  }}
                >
                  {getInitials(user?.name || user?.email)}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: "1rem", sm: "2rem" },
            maxWidth: "1400px",
            mx: "auto",
            width: "100%",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Welcome back, {user?.name?.split(" ")[0] || user?.email || "User"}
              !
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Here's what's happening with your batches today.
            </Typography>
          </Box>

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3} alignItems="stretch">
              {filteredBatches.map((batch) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={batch.BatchId}
                  sx={{ display: "flex" }}
                >
                  <DashboardCard
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 3,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" mb={2}>
                        <Box>
                          <Typography variant="h5" fontWeight={700}>
                            Batch Id: {batch.BatchId}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Batch Code: {batch.BatchCode}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, batch)}
                          sx={{ color: "text.secondary" }}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>

                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Job Role: {batch.JobRole}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={0.5}
                      >
                        Sector: {batch.Sector}
                      </Typography>

                      <Box mt={1}>
                        <Chip
                          label={batch.Status ?? "Not specified"}
                          color={
                            batch.Status === "Completed"
                              ? "success"
                              : batch.Status === "Ongoing"
                              ? "primary"
                              : batch.Status === "On Hold"
                              ? "warning"
                              : "default"
                          }
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                    </CardContent>
                  </DashboardCard>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Batch Options Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: { minWidth: 200, borderRadius: 2, boxShadow: 3, p: 0.5 },
            }}
          >
            <MenuItem
              onClick={() => handleOptionClick("View Details", selectedBatch)}
            >
              <Visibility sx={{ mr: 1.5, color: "text.secondary" }} /> View
              Details
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate(
                  `/candidate-details/${encodeURIComponent(
                    selectedBatch.BatchCode
                  )}`
                );
                handleClose();
              }}
            >
              <Person sx={{ mr: 1.5, color: "primary.main" }} /> Candidate
              Details
            </MenuItem>

            <MenuItem
              onClick={() => {
                navigate(
                  `/update-score/${encodeURIComponent(selectedBatch.BatchCode)}`
                );
                handleClose();
              }}
            >
              <Edit sx={{ mr: 1.5, color: "secondary.main" }} />
              Assessment Score
            </MenuItem>
          </Menu>

          {/* Profile Menu */}
          <Menu
            anchorEl={profileAnchorEl}
            open={isProfileMenuOpen}
            onClose={handleProfileClose}
          >
            <Typography variant="subtitle1" sx={{ px: 2, py: 1 }}>
              Hi, {user?.name?.split(" ")[0] || user?.email || "User"}!
            </Typography>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2" color="error">
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <Snackbar
        open={logoutSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{ width: "100%" }}
        >
          You have been logged out.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
